/**
 * MUI RoofGrid component that replaces the styled-components RoofGrid component
 * 
 * This component provides a Material UI equivalent of the original RoofGrid component
 * with similar props and functionality.
 */
import React from "react";
import { styled } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { getConditionResult } from "reduxModules/selectors/containerSelectors";
import { getDrawingBox } from "reduxModules/ducks/ui";
import { 
  MuiRoofGridProps, 
  RoofGridGroupProps, 
  GridSpacing, 
  GridOffset 
} from "types/components/elements";
import { RootState } from "types/redux/state";

/**
 * MUI RoofGrid component
 * 
 * Renders a grid pattern for the roof visualization
 */
const MuiRoofGrid: React.FC<MuiRoofGridProps> = (props) => {
  const usingGrid = useSelector((state: RootState) => state.container.roofGrid);
  const { x, y } = useSelector((state: RootState) => state.container.roofGridSpacing as GridSpacing);
  const { dx, dy } = useSelector((state: RootState) => state.container.roofGridOffset as GridOffset);

  const { bbox, scale } = useSelector(getDrawingBox);
  const visible = useSelector((state: RootState) => getConditionResult(state, {...props.show}));
  const active = useSelector((state: RootState) => getConditionResult(state, {...props.active}));

  const adjX = (dx ? dx : x) * scale;
  const adjY = (dy ? dy : y) * scale;

  const dot = 1;
  const x0 = bbox.x0 + adjX - dot;
  const y0 = bbox.y0 + adjY - dot;
  const xStep = x * scale;
  const yStep = y * scale;
  const width = bbox.width - adjX;
  const height = bbox.height - adjY;

  return (
    usingGrid && visible && (
      <RoofGridGroup id="ceiling" active={active}>
        <pattern 
          id="roof-grid-pattern" 
          x={x0} 
          y={y0} 
          width={xStep} 
          height={yStep} 
          patternUnits="userSpaceOnUse" 
          patternContentUnits="userSpaceOnUse"
        >
          <circle cx={dot} cy={dot} r={dot} />
        </pattern>
        <rect 
          x={x0} 
          y={y0} 
          width={width} 
          height={height} 
          fill="url(#roof-grid-pattern)"
        />
      </RoofGridGroup>
    )
  );
};

// Styled components using MUI styled API
const RoofGridGroup = styled('g', {
  shouldForwardProp: (prop) => prop !== 'active',
})<RoofGridGroupProps>(({ theme, active }) => ({
  fill: active ? '#9e9e9e' : '#e0e0e0',
  shapeRendering: 'geometricPrecision',
  opacity: active ? 0.8 : 0.3,
}));

export default MuiRoofGrid;
